import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Eye, EyeOff, KeyRound, Mail, Shield, TrendingUp } from 'lucide-react';

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        parameters: {
          sitekey: string;
          callback?: (token: string) => void;
          action?: string;
          'expired-callback'?: () => void;
          'error-callback'?: (errorCode?: string) => void;
        }
      ) => number;
      reset: (widgetId?: number) => void;
    };
    __turnstileOnLoad?: () => void;
  }
}

interface PasswordResetConfig {
  captchaEnabled: boolean;
  captchaSiteKey: string;
}

interface LoginProtectionConfig {
  captchaEnabled: boolean;
  captchaSiteKey: string;
}

interface LoginResult {
  errorMessage: string | null;
  requiresCaptcha?: boolean;
}

interface LoginScreenProps {
  onLogin: (username: string, password: string, captchaToken: string) => Promise<LoginResult>;
  onLoadLoginProtectionConfig: () => Promise<LoginProtectionConfig>;
  onLoadPasswordResetConfig: () => Promise<PasswordResetConfig>;
  onPasswordResetRequest: (email: string, captchaToken: string) => Promise<string | null>;
  onPasswordResetVerify: (token: string) => Promise<string | null>;
  onPasswordResetConfirm: (token: string, password: string, passwordConfirm: string) => Promise<string | null>;
}

type ViewMode = 'landing' | 'login' | 'help' | 'forgotPassword' | 'requestSent' | 'resetPassword' | 'resetComplete';

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const COMPANY_URL = 'https://en-ds.jp/';

const LoginScreen: React.FC<LoginScreenProps> = ({
  onLogin,
  onLoadLoginProtectionConfig,
  onLoadPasswordResetConfig,
  onPasswordResetRequest,
  onPasswordResetVerify,
  onPasswordResetConfirm,
}) => {
  const captchaContainerRef = useRef<HTMLDivElement | null>(null);
  const captchaWidgetIdRef = useRef<number | null>(null);
  const loginCaptchaContainerRef = useRef<HTMLDivElement | null>(null);
  const loginCaptchaWidgetIdRef = useRef<number | null>(null);
  const [currentView, setCurrentView] = useState<ViewMode>('landing');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginProtectionConfig, setLoginProtectionConfig] = useState<LoginProtectionConfig>({
    captchaEnabled: true,
    captchaSiteKey: '',
  });
  const [isLoginProtectionConfigLoaded, setIsLoginProtectionConfigLoaded] = useState(false);
  const [passwordResetConfig, setPasswordResetConfig] = useState<PasswordResetConfig>({
    captchaEnabled: true,
    captchaSiteKey: '',
  });
  const [loginCaptchaToken, setLoginCaptchaToken] = useState('');
  const [resetRequestEmail, setResetRequestEmail] = useState('');
  const [requestMessage, setRequestMessage] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaLoadError, setCaptchaLoadError] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [resetTokenError, setResetTokenError] = useState('');
  const [isVerifyingToken, setIsVerifyingToken] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showNewPasswordConfirm, setShowNewPasswordConfirm] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState('');

  const hasCaptchaSetupError = useMemo(
    () => passwordResetConfig.captchaEnabled && !passwordResetConfig.captchaSiteKey,
    [passwordResetConfig],
  );
  const hasLoginCaptchaSetupError = useMemo(
    () => loginProtectionConfig.captchaEnabled && !loginProtectionConfig.captchaSiteKey,
    [loginProtectionConfig],
  );

  const syncViewFromLocation = () => {
    const params = new URLSearchParams(window.location.search);
    const nextResetToken = params.get('resetToken')?.trim() || '';
    const screen = params.get('screen');
    if (nextResetToken) {
      setResetToken(nextResetToken);
      setCurrentView('resetPassword');
      return;
    }
    if (screen === 'forgot-password') {
      setCurrentView('forgotPassword');
      return;
    }
    setCurrentView('landing');
  };

  const navigateToView = (nextView: ViewMode, options?: { resetToken?: string }) => {
    const url = new URL(window.location.href);
    url.searchParams.delete('screen');
    url.searchParams.delete('resetToken');
    if (nextView === 'forgotPassword' || nextView === 'requestSent') {
      url.searchParams.set('screen', 'forgot-password');
    }
    if (nextView === 'resetPassword' && options?.resetToken) {
      url.searchParams.set('resetToken', options.resetToken);
    }
    window.history.pushState({}, '', `${url.pathname}${url.search}`);
    if (options?.resetToken !== undefined) {
      setResetToken(options.resetToken);
    }
    setCurrentView(nextView);
  };

  const navigateToLogin = () => {
    window.history.pushState({}, '', window.location.pathname);
    setCurrentView('login');
  };

  useEffect(() => {
    syncViewFromLocation();
    const handlePopState = () => syncViewFromLocation();
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    onLoadLoginProtectionConfig()
      .then((config) => setLoginProtectionConfig(config))
      .catch(() => {
        setLoginProtectionConfig({
          captchaEnabled: true,
          captchaSiteKey: '',
        });
      })
      .finally(() => setIsLoginProtectionConfigLoaded(true));
  }, [onLoadLoginProtectionConfig]);

  useEffect(() => {
    onLoadPasswordResetConfig()
      .then((config) => setPasswordResetConfig(config))
      .catch(() => {
        setPasswordResetConfig({
          captchaEnabled: true,
          captchaSiteKey: '',
        });
      });
  }, [onLoadPasswordResetConfig]);

  useEffect(() => {
    if (currentView !== 'forgotPassword') {
      return;
    }
    if (!passwordResetConfig.captchaEnabled || !passwordResetConfig.captchaSiteKey || !captchaContainerRef.current) {
      return;
    }

    const renderCaptcha = () => {
      if (!window.turnstile || captchaWidgetIdRef.current !== null) {
        return;
      }
      try {
        captchaWidgetIdRef.current = window.turnstile.render('#password-reset-turnstile', {
          sitekey: passwordResetConfig.captchaSiteKey,
          action: 'password_reset_request',
          callback: (token: string) => setCaptchaToken(token),
          'expired-callback': () => setCaptchaToken(''),
          'error-callback': (errorCode?: string) => {
            setCaptchaToken('');
            setCaptchaLoadError(errorCode ? `Turnstile の初期化に失敗しました: ${errorCode}` : 'Turnstile の初期化に失敗しました。');
          },
        });
        setCaptchaLoadError('');
      } catch (_error) {
        setCaptchaToken('');
        setCaptchaLoadError('Turnstile の描画に失敗しました。ページを再読み込みしてください。');
      }
    };

    const existingScript = document.querySelector<HTMLScriptElement>('script[data-turnstile-script="true"]');
    if (existingScript) {
      if (window.turnstile) {
        renderCaptcha();
      } else {
        const handleLoad = () => renderCaptcha();
        existingScript.addEventListener('load', handleLoad, { once: true });
        return () => existingScript.removeEventListener('load', handleLoad);
      }
      return;
    }

    window.__turnstileOnLoad = () => renderCaptcha();
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=__turnstileOnLoad';
    script.async = true;
    script.defer = true;
    script.dataset.turnstileScript = 'true';
    script.onerror = () => {
      setCaptchaLoadError('Turnstile スクリプトの読み込みに失敗しました。ネットワーク設定を確認してください。');
    };
    document.body.appendChild(script);
    return () => {
      window.__turnstileOnLoad = undefined;
    };
  }, [currentView, passwordResetConfig]);

  useEffect(() => {
    if (currentView !== 'login') {
      return;
    }
    if (!loginProtectionConfig.captchaEnabled || !loginProtectionConfig.captchaSiteKey || !loginCaptchaContainerRef.current) {
      return;
    }

    const renderCaptcha = () => {
      if (!window.turnstile || loginCaptchaWidgetIdRef.current !== null) {
        return;
      }
      try {
        loginCaptchaWidgetIdRef.current = window.turnstile.render('#login-turnstile', {
          sitekey: loginProtectionConfig.captchaSiteKey,
          action: 'login',
          callback: (token: string) => setLoginCaptchaToken(token),
          'expired-callback': () => setLoginCaptchaToken(''),
          'error-callback': () => {
            setLoginCaptchaToken('');
            setCaptchaLoadError('Turnstile の初期化に失敗しました。');
          },
        });
        setCaptchaLoadError('');
      } catch (_error) {
        setLoginCaptchaToken('');
        setCaptchaLoadError('Turnstile の描画に失敗しました。ページを再読み込みしてください。');
      }
    };

    const existingScript = document.querySelector<HTMLScriptElement>('script[data-turnstile-script="true"]');
    if (existingScript) {
      if (window.turnstile) {
        renderCaptcha();
      } else {
        const handleLoad = () => renderCaptcha();
        existingScript.addEventListener('load', handleLoad, { once: true });
        return () => existingScript.removeEventListener('load', handleLoad);
      }
      return;
    }

    window.__turnstileOnLoad = () => renderCaptcha();
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=__turnstileOnLoad';
    script.async = true;
    script.defer = true;
    script.dataset.turnstileScript = 'true';
    script.onerror = () => {
      setCaptchaLoadError('Turnstile スクリプトの読み込みに失敗しました。ネットワーク設定を確認してください。');
    };
    document.body.appendChild(script);
    return () => {
      window.__turnstileOnLoad = undefined;
    };
  }, [currentView, loginProtectionConfig]);

  useEffect(() => {
    if (currentView !== 'resetPassword' || !resetToken) {
      return;
    }

    let isMounted = true;
    setIsVerifyingToken(true);
    setResetTokenError('');
    onPasswordResetVerify(resetToken).then((nextError) => {
      if (!isMounted) {
        return;
      }
      setResetTokenError(nextError || '');
      setIsVerifyingToken(false);
    });
    return () => {
      isMounted = false;
    };
  }, [currentView, resetToken, onPasswordResetVerify]);

  const resetCaptchaWidget = () => {
    setCaptchaToken('');
    if (captchaWidgetIdRef.current !== null && window.turnstile) {
      window.turnstile.reset(captchaWidgetIdRef.current);
    }
  };

  const resetLoginCaptchaWidget = () => {
    setLoginCaptchaToken('');
    if (loginCaptchaWidgetIdRef.current !== null && window.turnstile) {
      window.turnstile.reset(loginCaptchaWidgetIdRef.current);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError('');
    setCaptchaLoadError('');

    if (!isLoginProtectionConfigLoaded) {
      setError('CAPTCHA の設定を読み込んでいます。しばらくお待ちください。');
      setIsSubmitting(false);
      return;
    }

    if (loginProtectionConfig.captchaEnabled && !loginCaptchaToken) {
      setError(hasLoginCaptchaSetupError ? 'CAPTCHA の設定を確認してください。' : 'CAPTCHA を完了してください。');
      setIsSubmitting(false);
      return;
    }

    const result = await onLogin(username.trim(), password, loginCaptchaToken);
    if (result.errorMessage && loginProtectionConfig.captchaEnabled) {
      resetLoginCaptchaWidget();
    }
    if (result.errorMessage) {
      setError(result.errorMessage);
    }
    setIsSubmitting(false);
  };

  const handlePasswordResetRequestSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError('');
    setCaptchaLoadError('');

    const normalizedEmail = resetRequestEmail.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(normalizedEmail)) {
      setError('メールアドレスの形式が正しくありません。');
      setIsSubmitting(false);
      return;
    }

    if (passwordResetConfig.captchaEnabled && !captchaToken) {
      setError(hasCaptchaSetupError ? 'CAPTCHA の設定を確認してください。' : 'CAPTCHA を完了してください。');
      setIsSubmitting(false);
      return;
    }

    const message = await onPasswordResetRequest(normalizedEmail, captchaToken);
    if (message && message !== 'メールアドレスが正しい場合、メールが届きます。') {
      setError(message);
      resetCaptchaWidget();
      setIsSubmitting(false);
      return;
    }

    setRequestMessage(message || 'メールアドレスが正しい場合、メールが届きます。');
    resetCaptchaWidget();
    setIsSubmitting(false);
    navigateToView('requestSent');
  };

  const handlePasswordResetConfirmSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    setResetPasswordError('');
    const nextMessage = await onPasswordResetConfirm(resetToken, newPassword, newPasswordConfirm);
    if (nextMessage && nextMessage !== 'password_reset_success') {
      setResetPasswordError(nextMessage);
      setIsSubmitting(false);
      return;
    }
    window.history.replaceState({}, '', window.location.pathname);
    setResetToken('');
    setNewPassword('');
    setNewPasswordConfirm('');
    setIsSubmitting(false);
    setCurrentView('resetComplete');
  };

  const renderLanding = () => (
    <div className="public-screen public-screen-landing">
      <div className="landing-container">
        <div className="landing-hero">
          <div className="hero-content">
            <h1 className="hero-title">
              <TrendingUp className="hero-icon" />
              入札データ分析プラットフォーム
            </h1>
            <p className="hero-subtitle">
              ログイン認証とデータベース保存に対応した DataLink へようこそ。
            </p>
            <div className="hero-buttons">
              <button className="btn btn-primary" onClick={navigateToLogin}>
                ログイン
                <ArrowRight size={20} />
              </button>
              <button className="btn btn-secondary" onClick={() => navigateToView('forgotPassword')}>
                パスワードを忘れた場合
              </button>
              <button className="btn btn-secondary" onClick={() => setCurrentView('help')}>
                利用案内
              </button>
            </div>
          </div>
          <div className="hero-image">
            <div className="hero-chart">
              <Shield size={120} className="chart-icon" />
              <p style={{ marginTop: '1rem', color: '#374151', fontWeight: 600 }}>認証済みユーザーのみアクセス可能</p>
            </div>
          </div>
        </div>
      </div>
      <div className="public-footer">
        <span dangerouslySetInnerHTML={{ __html: '&copy; 2026 ' }} />
        <a href={COMPANY_URL} target="_blank" rel="noopener noreferrer">株式会社ｅｎ</a>
      </div>
    </div>
  );

  const renderLogin = () => (
    <div className="public-screen">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <TrendingUp className="auth-icon" />
            <h2>ログイン</h2>
            <p>管理者から共有されたユーザー名とパスワードを入力してください</p>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="username">ユーザー名</label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                placeholder="username"
                autoComplete="username"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">パスワード</label>
              <div className="password-input">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="password"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label="パスワード表示切替"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {isLoginProtectionConfigLoaded && loginProtectionConfig.captchaEnabled && (
              <div className="captcha-panel">
                {hasLoginCaptchaSetupError ? (
                  <div className="error-message">CAPTCHA の設定が不足しています。</div>
                ) : (
                  <div id="login-turnstile" ref={loginCaptchaContainerRef} className="captcha-widget" />
                )}
              </div>
            )}

            {captchaLoadError && <div className="error-message">{captchaLoadError}</div>}
            {error && <div className="error-message">{error}</div>}

            <button
              type="submit"
              className="btn btn-primary btn-full auth-submit-button"
              disabled={isSubmitting || !isLoginProtectionConfigLoaded}
            >
              {isSubmitting ? 'ログイン中...' : !isLoginProtectionConfigLoaded ? '認証設定を読込中...' : 'ログイン'}
            </button>
          </form>

          <div className="auth-footer">
            <p>アカウント発行が必要な場合は管理者に連絡してください。</p>
            <p style={{ marginTop: '0.5rem' }}>
              <button className="link-button" onClick={() => navigateToView('forgotPassword')}>
                パスワードを忘れた場合
              </button>
            </p>
            <p style={{ marginTop: '0.5rem' }}>
              <button className="link-button" onClick={() => setCurrentView('help')}>
                ログインできない場合はこちら
              </button>
            </p>
          </div>
        </div>

        <button className="back-button" onClick={() => navigateToView('landing')}>
          ← ホームに戻る
        </button>
      </div>
      <div className="public-footer">
        <span dangerouslySetInnerHTML={{ __html: '&copy; 2026 ' }} />
        <a href={COMPANY_URL} target="_blank" rel="noopener noreferrer">株式会社ｅｎ</a>
      </div>
    </div>
  );

  const renderHelp = () => (
    <div className="public-screen">
      <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <Shield className="auth-icon" />
          <h2>利用案内</h2>
          <p>本システムは管理者が発行したアカウントでのみ利用できます。</p>
        </div>
        <div style={{ display: 'grid', gap: '0.75rem', color: '#4b5563', lineHeight: 1.7 }}>
          <p>1. 管理者からユーザー名とパスワードを受け取ってください。</p>
          <p>2. パスワードを忘れた場合は、登録済みメールアドレスから再設定してください。</p>
          <p>3. アップロードしたファイルはデータベースに保存され、次回ログイン後も引き続き利用できます。</p>
        </div>
      </div>

      <button className="back-button" onClick={() => navigateToView('landing')}>
        ← ホームに戻る
      </button>
      </div>
      <div className="public-footer">
        <span dangerouslySetInnerHTML={{ __html: '&copy; 2026 ' }} />
        <a href={COMPANY_URL} target="_blank" rel="noopener noreferrer">株式会社ｅｎ</a>
      </div>
    </div>
  );

  const renderForgotPassword = () => (
    <div className="public-screen">
      <div className="auth-container">
      <div className="auth-card auth-card-wide">
        <div className="auth-header">
          <Mail className="auth-icon" />
          <h2>パスワード再設定</h2>
          <p>登録済みメールアドレスを入力してください。</p>
        </div>

        <form onSubmit={handlePasswordResetRequestSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="reset-email">メールアドレス</label>
            <input
              type="email"
              id="reset-email"
              value={resetRequestEmail}
              onChange={(event) => setResetRequestEmail(event.target.value)}
              placeholder="name@example.com"
              autoComplete="email"
              required
            />
          </div>

          <p className="auth-note">メールアドレスが正しい場合、メールが届きます。</p>

          {passwordResetConfig.captchaEnabled && (
            <div className="captcha-panel">
              {hasCaptchaSetupError ? (
                <div className="error-message">CAPTCHA の設定が不足しています。</div>
              ) : (
                <div id="password-reset-turnstile" ref={captchaContainerRef} className="captcha-widget" />
              )}
            </div>
          )}

          {captchaLoadError && <div className="error-message">{captchaLoadError}</div>}
          {error && <div className="error-message">{error}</div>}

          <button type="submit" className="btn btn-primary btn-full auth-submit-button" disabled={isSubmitting}>
            {isSubmitting ? '送信中...' : '再設定メールを送る'}
          </button>
        </form>
      </div>

      <button className="back-button" onClick={navigateToLogin}>
        ← ログインへ戻る
      </button>
      </div>
      <div className="public-footer">
        <span dangerouslySetInnerHTML={{ __html: '&copy; 2026 ' }} />
        <a href={COMPANY_URL} target="_blank" rel="noopener noreferrer">株式会社ｅｎ</a>
      </div>
    </div>
  );

  const renderRequestSent = () => (
    <div className="public-screen">
      <div className="auth-container">
      <div className="auth-card auth-card-wide">
        <div className="auth-header">
          <Mail className="auth-icon" />
          <h2>メールを送信しました</h2>
          <p>{requestMessage || 'メールアドレスが正しい場合、メールが届きます。'}</p>
        </div>

        <div className="auth-info-block">
          <p>受信したメールの URL を開き、新しいパスワードを設定してください。</p>
          <p>再設定 URL の有効期限は 10 分です。</p>
        </div>

        <button className="btn btn-primary btn-full auth-submit-button" onClick={navigateToLogin}>
          ログイン画面へ戻る
        </button>
      </div>

      <button className="back-button" onClick={() => navigateToView('landing')}>
        ← ホームに戻る
      </button>
      </div>
      <div className="public-footer">
        <span dangerouslySetInnerHTML={{ __html: '&copy; 2026 ' }} />
        <a href={COMPANY_URL} target="_blank" rel="noopener noreferrer">株式会社ｅｎ</a>
      </div>
    </div>
  );

  const renderResetPassword = () => (
    <div className="public-screen">
      <div className="auth-container">
      <div className="auth-card auth-card-wide">
        <div className="auth-header">
          <KeyRound className="auth-icon" />
          <h2>新しいパスワード設定</h2>
          <p>新しいパスワードを 2 回入力してください。</p>
        </div>

        {isVerifyingToken ? (
          <div className="auth-info-block">
            <p>再設定 URL を確認しています...</p>
          </div>
        ) : resetTokenError ? (
          <div className="error-message">{resetTokenError}</div>
        ) : (
          <form onSubmit={handlePasswordResetConfirmSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="new-password">新しいパスワード</label>
              <div className="password-input">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  id="new-password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowNewPassword((prev) => !prev)}
                  aria-label="新しいパスワード表示切替"
                >
                  {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="new-password-confirm">新しいパスワード（再入力）</label>
              <div className="password-input">
                <input
                  type={showNewPasswordConfirm ? 'text' : 'password'}
                  id="new-password-confirm"
                  value={newPasswordConfirm}
                  onChange={(event) => setNewPasswordConfirm(event.target.value)}
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowNewPasswordConfirm((prev) => !prev)}
                  aria-label="確認用パスワード表示切替"
                >
                  {showNewPasswordConfirm ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <p className="auth-note">8文字以上で、英字と数字を両方含めてください。</p>

            {resetPasswordError && <div className="error-message">{resetPasswordError}</div>}

            <button type="submit" className="btn btn-primary btn-full auth-submit-button" disabled={isSubmitting}>
              {isSubmitting ? '変更中...' : '変更をする'}
            </button>
          </form>
        )}
      </div>

      <button className="back-button" onClick={() => navigateToView('landing')}>
        ← ホームに戻る
      </button>
      </div>
      <div className="public-footer">
        <span dangerouslySetInnerHTML={{ __html: '&copy; 2026 ' }} />
        <a href={COMPANY_URL} target="_blank" rel="noopener noreferrer">株式会社ｅｎ</a>
      </div>
    </div>
  );

  const renderResetComplete = () => (
    <div className="public-screen">
      <div className="auth-container">
      <div className="auth-card auth-card-wide">
        <div className="auth-header">
          <Shield className="auth-icon" />
          <h2>パスワードを変更しました</h2>
          <p>新しいパスワードでログインしてください。</p>
        </div>
        <button className="btn btn-primary btn-full auth-submit-button" onClick={navigateToLogin}>
          ログイン画面へ戻る
        </button>
      </div>

      <button className="back-button" onClick={() => navigateToView('landing')}>
        ← ホームに戻る
      </button>
      </div>
      <div className="public-footer">
        <span dangerouslySetInnerHTML={{ __html: '&copy; 2026 ' }} />
        <a href={COMPANY_URL} target="_blank" rel="noopener noreferrer">株式会社ｅｎ</a>
      </div>
    </div>
  );

  switch (currentView) {
    case 'login':
      return renderLogin();
    case 'help':
      return renderHelp();
    case 'forgotPassword':
      return renderForgotPassword();
    case 'requestSent':
      return renderRequestSent();
    case 'resetPassword':
      return renderResetPassword();
    case 'resetComplete':
      return renderResetComplete();
    case 'landing':
    default:
      return renderLanding();
  }
};

export default LoginScreen;
